import { reactive } from 'vue'
import { applyMomentTimezone } from '@/libs/companyTime'
import { resolvePictureUrl } from '@/libs/pictureUrl'

const DEFAULT_TITLE = 'AGVA'
const FALLBACK_LOGO = '/static_images/no_image.png'

let defaultFaviconHref = null

/** Reactive branding for Vue UI (sidebar logo, etc.). */
export const companyBranding = reactive({
  name: null,
  logoUrl: typeof window !== 'undefined' && window.companyLogo
    ? resolvePictureUrl(window.companyLogo)
    : FALLBACK_LOGO,
})

function syncBrandingState({ name = null, logoUrl = FALLBACK_LOGO } = {}) {
  companyBranding.name = name
  companyBranding.logoUrl = logoUrl || FALLBACK_LOGO
}

function getDefaultFaviconHref() {
  if (typeof document === 'undefined') {
    return '/favicon.ico'
  }
  if (defaultFaviconHref) {
    return defaultFaviconHref
  }
  const existing = document.querySelector("link[rel='icon'], link[rel='shortcut icon']")
  defaultFaviconHref = (existing && existing.getAttribute('href')) || '/favicon.ico'
  return defaultFaviconHref
}

function isUsableCompanyLogo(url) {
  if (!url) {
    return false
  }
  const value = String(url).trim().toLowerCase()
  if (!value || value === FALLBACK_LOGO) {
    return false
  }
  return !value.includes('no_image') && !value.includes('unisex-avatar')
}

function setFavicon(href) {
  if (typeof document === 'undefined' || !href) {
    return
  }

  const links = document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']")
  if (links.length) {
    links.forEach((link) => {
      link.setAttribute('href', href)
      // Prefer type guess for png/jpg logos used as favicons.
      if (/\.jpe?g($|\?)/i.test(href)) {
        link.setAttribute('type', 'image/jpeg')
      } else if (/\.png($|\?)/i.test(href)) {
        link.setAttribute('type', 'image/png')
      } else if (/\.svg($|\?)/i.test(href)) {
        link.setAttribute('type', 'image/svg+xml')
      } else if (/\.ico($|\?)/i.test(href)) {
        link.setAttribute('type', 'image/x-icon')
      }
    })
    return
  }

  const link = document.createElement('link')
  link.rel = 'icon'
  link.href = href
  document.head.appendChild(link)
}

export function applyDocumentBranding({ name = null, logoUrl = null } = {}) {
  syncBrandingState({
    name: name && String(name).trim() ? String(name).trim() : null,
    logoUrl: logoUrl || FALLBACK_LOGO,
  })

  if (typeof document === 'undefined') {
    return
  }

  document.title = companyBranding.name || DEFAULT_TITLE

  const favicon = isUsableCompanyLogo(logoUrl) ? logoUrl : getDefaultFaviconHref()
  setFavicon(favicon)
}

export function applyCompanyWindowGlobals(company) {
  if (!company) {
    return
  }

  window.companyPurpose = company.purpose
  window.companyId = company.id
  window.companyName = company.name || null
  window.companyLogo = resolvePictureUrl(company.picture)
  window.companyCountryId = company.country_id
  window.companyCountryCode = company.country_short_name
    ? String(company.country_short_name).toLowerCase()
    : 'us'
  window.companyPhoneCode = company.country_phone_code || ''
  window.currency_symbol = company.currency_symbol || '$'
  window.timezone_name = company.timezone_name || null
  window.time_format = company.time_format === '24h' ? '24h' : '12h'
  window.saleTax = {
    name: company.tax_name,
    value: company.tax_value,
    id: company.tax_id,
  }

  applyMomentTimezone(window.timezone_name || 'UTC')
  applyDocumentBranding({
    name: window.companyName,
    logoUrl: window.companyLogo,
  })
}

export function clearCompanyWindowGlobals() {
  window.companyPurpose = null
  window.companyId = null
  window.companyName = null
  window.companyLogo = FALLBACK_LOGO
  window.companyCountryId = null
  window.companyCountryCode = 'us'
  window.companyPhoneCode = ''
  window.currency_symbol = '$'
  window.timezone_name = null
  window.time_format = '12h'
  window.saleTax = null

  applyMomentTimezone('UTC')
  applyDocumentBranding()
}
